import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, ShoppingBag, Store, ShieldCheck, Banknote, KeyRound,
  Sparkles, CreditCard, Wallet, Eye, Activity, AlertOctagon, FileLock2,
  ScrollText, Gauge, Building2, Phone, Lock,
} from "lucide-react";
import BankDemoShell, { BankSponsorPill } from "./BankDemoShell";

// /bank-demo — the entry point for sponsor-bank reviewers.
// Single landing page: 2 large flow cards + a tabbed "What you'll see" panel
// covering all the regulatory touchpoints (KYB, AML, fraud, reserves, audit
// trail, transaction monitoring).

const PILLARS = [
  { icon: ShieldCheck, label: "KYB · Middesk",        sub: "Business verification, EIN match, OFAC scan" },
  { icon: KeyRound,    label: "Identity · KYC",       sub: "Beneficial owner DOB + last-4 SSN, 18+ check" },
  { icon: Phone,       label: "SMS Step-up · Twilio", sub: "OTP at signup + on every high-risk action" },
  { icon: Banknote,    label: "Bank link · Plaid",    sub: "Account ownership + 60-day cashflow signals" },
  { icon: Sparkles,    label: "AI Underwriter",       sub: "Verqo AI · explainable decisioning" },
  { icon: CreditCard,  label: "Net-30/60/90 issue",   sub: "Trade-credit instruments, no factor in middle" },
  { icon: Wallet,      label: "Wallet · TabPay",      sub: "Sub-ledger + instant ACH/RTP payouts to seller" },
  { icon: Lock,        label: "Encryption at rest",   sub: "AES-256 · per-tenant secrets · KMS rotation" },
];

export default function BankDemoLanding() {
  const [tab, setTab] = useState("fraud");
  return (
    <BankDemoShell crumbs={[{ label: "Bank Demo" }]}>
      {/* HERO ───────────────────────────────────────────────────────── */}
      <section className="grid md:grid-cols-12 gap-10 items-end pb-12 border-b border-white/10">
        <div className="md:col-span-7">
          <BankSponsorPill />
          <h1 className="mt-5 text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]">
            A sandbox tour of verqo's
            <br /> onboarding & risk surface,
            <br /> built for the sponsor bank.
          </h1>
          <p className="mt-5 max-w-xl text-[15.5px] text-white/65 leading-relaxed">
            Click through a real, end-to-end signup for both buyers and sellers. Every regulatory touchpoint —
            KYB, KYC, OFAC, Plaid, Twilio step-up, AI underwriting, audit trail, transaction monitoring — fires with
            mocked outcomes so you can review the controls without spending a dollar of compliance budget.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link
              to="/bank-demo/buyer"
              className="inline-flex items-center gap-2 rounded-full bg-white text-[#0b0d12] px-5 py-3 text-[14px] font-semibold hover:bg-white/90 transition-colors"
              data-testid="bank-demo-start-buyer"
            >
              <ShoppingBag className="w-4 h-4" />
              Start the buyer flow
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/bank-demo/seller"
              className="inline-flex items-center gap-2 rounded-full border border-white/25 px-5 py-3 text-[14px] font-medium hover:bg-white/5 transition-colors"
              data-testid="bank-demo-start-seller"
            >
              <Store className="w-4 h-4" />
              Start the seller flow
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
        <div className="md:col-span-5">
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
            <div className="flex items-center gap-2 text-[12px] text-white/65 uppercase tracking-wide mb-3">
              <Gauge className="w-3.5 h-3.5" /> Live demo metrics
            </div>
            <ul className="grid grid-cols-2 gap-y-4 gap-x-6">
              <Stat value="94 sec" label="Median time to credit decision" />
              <Stat value="0.42%" label="Fraud rate (Q4 2025)" />
              <Stat value="100%" label="OFAC + KYB coverage" />
              <Stat value="14:1"  label="Reserve coverage ratio" />
            </ul>
          </div>
        </div>
      </section>

      {/* TWO BIG FLOW CARDS ─────────────────────────────────────────── */}
      <section className="grid md:grid-cols-2 gap-6 mt-12" data-testid="bank-demo-flow-cards">
        <FlowCard
          to="/bank-demo/buyer"
          icon={ShoppingBag}
          eyebrow="Buyer flow · 6 steps"
          title="Wholesale buyer signup"
          sub="Account → KYB → KYC owner → Plaid → AI Underwriter → Net-30 line"
          bullets={[
            "Middesk KYB + OFAC scan run on every business",
            "Plaid bank-link uplifts the score with 60-day cashflow",
            "Verqo AI underwriter returns an explainable decision",
            "Reserve allocation + transaction-monitoring rule set live",
          ]}
          testid="bank-demo-buyer-card"
        />
        <FlowCard
          to="/bank-demo/seller"
          icon={Store}
          eyebrow="Seller flow · 5 steps"
          title="Brand & supplier signup"
          sub="Account → KYB → KYC owner → Payout bank → Wallet & terms ready"
          bullets={[
            "Same KYB/KYC stack as the buyer side",
            "Plaid links the payout bank used for D+1 settlements",
            "TabPay wallet provisioned with sub-ledger for instant payouts",
            "Funding sources whitelisted per AML/BSA program",
          ]}
          testid="bank-demo-seller-card"
        />
      </section>

      {/* PILLARS ───────────────────────────────────────────────────── */}
      <section className="mt-14">
        <h2 className="text-[13px] font-semibold tracking-wide uppercase text-white/65">What you'll see during the walkthrough</h2>
        <ul className="mt-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="bank-demo-pillars">
          {PILLARS.map((p) => (
            <li key={p.label} className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
              <p.icon className="w-5 h-5 text-emerald-300" />
              <div className="mt-3 text-[13.5px] font-semibold">{p.label}</div>
              <div className="text-[12px] text-white/60 mt-1 leading-snug">{p.sub}</div>
            </li>
          ))}
        </ul>
      </section>

      {/* SUPPORTING TABS ───────────────────────────────────────────── */}
      <section className="mt-14 rounded-2xl border border-white/10 bg-white/[0.03] overflow-hidden">
        <div className="flex flex-wrap border-b border-white/10" data-testid="bank-demo-tabs">
          {[
            { id: "fraud",     label: "Fraud controls", icon: AlertOctagon },
            { id: "reserves",  label: "Reserves & risk", icon: ShieldCheck },
            { id: "audit",     label: "Audit trail",     icon: ScrollText },
            { id: "txmon",     label: "Transaction monitoring", icon: Eye },
          ].map((t) => {
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-2 px-5 py-3.5 text-[13px] font-medium border-b-2 transition-colors ${
                  active
                    ? "border-white text-white bg-white/[0.04]"
                    : "border-transparent text-white/55 hover:text-white"
                }`}
                data-testid={`bank-demo-tab-${t.id}`}
              >
                <t.icon className="w-4 h-4" />
                {t.label}
              </button>
            );
          })}
        </div>
        <div className="p-6 md:p-8">
          {tab === "fraud" && <FraudPanel />}
          {tab === "reserves" && <ReservesPanel />}
          {tab === "audit" && <AuditPanelExplainer />}
          {tab === "txmon" && <TxMonPanel />}
        </div>
      </section>

      {/* CALL TO REVIEW ────────────────────────────────────────────── */}
      <section className="mt-14 rounded-2xl border border-emerald-500/20 bg-emerald-500/5 p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>
          <Building2 className="w-5 h-5 text-emerald-300" />
          <h3 className="mt-3 text-2xl font-semibold tracking-tight">Ready to walk it?</h3>
          <p className="mt-2 max-w-xl text-[14.5px] text-white/65">
            Pick either flow — both render the same compliance stack. Every click is sandboxed; no real bank, KYB,
            or SMS calls are placed during the demo.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Link
            to="/bank-demo/buyer"
            className="inline-flex items-center gap-2 rounded-full bg-white text-[#0b0d12] px-4 py-2.5 text-[13.5px] font-semibold hover:bg-white/90 transition-colors"
            data-testid="bank-demo-cta-buyer"
          >
            Start buyer flow <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to="/bank-demo/seller"
            className="inline-flex items-center gap-2 rounded-full border border-white/25 px-4 py-2.5 text-[13.5px] font-medium hover:bg-white/5 transition-colors"
            data-testid="bank-demo-cta-seller"
          >
            Start seller flow <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </BankDemoShell>
  );
}

function Stat({ value, label }) {
  return (
    <li>
      <div className="text-2xl font-semibold tracking-tight">{value}</div>
      <div className="text-[11.5px] text-white/55 leading-snug mt-0.5">{label}</div>
    </li>
  );
}

function FlowCard({ to, icon: Icon, eyebrow, title, sub, bullets, testid }) {
  return (
    <Link
      to={to}
      className="group rounded-2xl border border-white/10 bg-white/[0.03] p-7 hover:bg-white/[0.06] hover:border-white/20 transition-colors flex flex-col"
      data-testid={testid}
    >
      <div className="flex items-center justify-between">
        <div className="w-11 h-11 rounded-xl bg-white text-[#0b0d12] grid place-items-center">
          <Icon className="w-5 h-5" />
        </div>
        <span className="text-[11.5px] uppercase tracking-wide text-white/55">{eyebrow}</span>
      </div>
      <h3 className="mt-5 text-2xl font-semibold tracking-tight">{title}</h3>
      <p className="mt-1.5 text-[13px] text-white/55">{sub}</p>
      <ul className="mt-5 space-y-2 text-[13px] text-white/75">
        {bullets.map((b) => (
          <li key={b} className="flex items-start gap-2">
            <span className="mt-1 w-1 h-1 rounded-full bg-emerald-300 flex-shrink-0" />
            <span>{b}</span>
          </li>
        ))}
      </ul>
      <div className="mt-7 inline-flex items-center gap-2 text-[13.5px] font-semibold text-white/80 group-hover:text-white">
        Open this flow <ArrowRight className="w-4 h-4" />
      </div>
    </Link>
  );
}

// ─── Tab panels (static, but information-dense for bank reviewer) ──────────
function FraudPanel() {
  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="bank-demo-panel-fraud">
      <div>
        <h3 className="text-lg font-semibold tracking-tight">Layered fraud controls</h3>
        <p className="mt-2 text-[14px] text-white/65 leading-relaxed">
          We treat onboarding and transactional fraud as two separate problems. Onboarding is gated by deterministic
          identity controls; transactional risk is monitored continuously with rule + model fusion.
        </p>
        <ul className="mt-4 space-y-3 text-[13.5px]">
          <Row k="Bot gate" v="Cloudflare Turnstile on every public form (signup, /demo, /p/po/:token)" />
          <Row k="Velocity" v="≤10 demo launches/IP/hour · ≤5 signup attempts/email/hour" />
          <Row k="Email & phone" v="MX + carrier-type checks · Twilio Verify OTP at signup + on step-up actions" />
          <Row k="Identity" v="Middesk KYB · OFAC SDN + Sectoral Sanctions list · 18+ DOB validation" />
          <Row k="Device fingerprint" v="Device-trust cookie · 30-day rotation · forced re-step-up on new device" />
          <Row k="Transactional" v="Rule engine + Plaid cashflow score + Verqo AI reasoning over each invoice" />
        </ul>
      </div>
      <div className="rounded-xl border border-white/10 bg-[#070910] p-5">
        <div className="text-[11.5px] uppercase tracking-wide text-white/55">Sample SAR-grade timeline</div>
        <ol className="mt-4 space-y-3 text-[13px]">
          {[
            ["10:01:04", "Signup attempt", "buyer@apex.com from 73.114.x.x — passed Turnstile, OTP sent"],
            ["10:01:48", "OTP verified", "Twilio Verify · code matched · trust cookie issued (30d)"],
            ["10:02:11", "KYB pulled", "Middesk · EIN 84-2156033 matches Apex Goods LLC · SOS active"],
            ["10:02:14", "OFAC scan", "Owner DOB 1989-04-12 last-4 0143 · 0 hits SDN, 0 sectoral"],
            ["10:02:55", "Plaid linked", "Item · Chase · 4 accounts · 62 days history · ACH-eligible"],
            ["10:03:09", "AI decision", "Net-30 $25K · risk band B · explainability stored to /audit/{decision}"],
          ].map(([t, evt, sub]) => (
            <li key={t} className="grid grid-cols-[60px_1fr] gap-3">
              <span className="font-mono text-[12px] text-white/45">{t}</span>
              <div>
                <div className="text-white">{evt}</div>
                <div className="text-white/55 text-[12px] mt-0.5 leading-snug">{sub}</div>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}

function ReservesPanel() {
  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="bank-demo-panel-reserves">
      <div>
        <h3 className="text-lg font-semibold tracking-tight">Reserve & risk management</h3>
        <p className="mt-2 text-[14px] text-white/65 leading-relaxed">
          Every credit decision is backed by a per-buyer reserve allocation. We hold reserves at the platform-wide
          level (master) and route them down to buyer accounts as they draw. The sponsor bank holds the FBO, we hold
          the program reserve.
        </p>
        <ul className="mt-4 space-y-3 text-[13.5px]">
          <Row k="Program reserve" v="14× expected monthly loss (Q4 actuals: 0.42% net charge-offs)" />
          <Row k="Per-buyer reserve" v="Tier × utilization × weighted age — recomputed daily" />
          <Row k="Concentration" v="Single buyer ≤ 5% of program · single supplier ≤ 8%" />
          <Row k="Allianz Trade" v="Optional credit insurance wraps Net-60+ exposures · 90% indemnity" />
          <Row k="Cooling period" v="New buyers: graduated limits — Net-30 first, Net-60 unlocks at 60d clean history" />
        </ul>
      </div>
      <div className="rounded-xl border border-white/10 bg-[#070910] p-5">
        <div className="text-[11.5px] uppercase tracking-wide text-white/55">Snapshot · program reserve waterfall</div>
        <table className="mt-4 w-full text-[13px]">
          <thead>
            <tr className="text-white/55 text-[11.5px] uppercase tracking-wide">
              <th className="text-left font-medium pb-2">Bucket</th>
              <th className="text-right font-medium pb-2">Balance</th>
              <th className="text-right font-medium pb-2">Coverage</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {[
              ["Active credit lines (gross)",  "$48.2M", "—"],
              ["Program reserve (verqo)",      "$2.05M", "4.25%"],
              ["Per-buyer rolling reserves",   "$890K",  "1.85%"],
              ["Allianz wrap (Net-60+ only)",  "$11.8M", "90%"],
              ["Net at-risk to sponsor bank",  "$1.32M", "2.74%"],
            ].map(([k, v, c], i) => (
              <tr key={k} className={i === 4 ? "text-emerald-300 font-semibold" : ""}>
                <td className="py-2.5">{k}</td>
                <td className="py-2.5 text-right font-mono">{v}</td>
                <td className="py-2.5 text-right font-mono">{c}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AuditPanelExplainer() {
  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="bank-demo-panel-audit">
      <div>
        <h3 className="text-lg font-semibold tracking-tight">Append-only audit trail</h3>
        <p className="mt-2 text-[14px] text-white/65 leading-relaxed">
          Every state-changing event in the platform — from a signup field edit to a payout settlement — writes a
          record to an append-only audit log. Records are immutable, timestamped to UTC at write, and exportable as
          a signed JSONL bundle for the bank's regulatory archive.
        </p>
        <ul className="mt-4 space-y-3 text-[13.5px]">
          <Row k="Storage" v="MongoDB time-series collection · WORM (no _delete grant) · 7-year retention" />
          <Row k="Schema" v="actor (user/system) · action · resource (collection:id) · status · ip · ua · meta" />
          <Row k="Export" v="Signed JSONL (Ed25519) · per-tenant scope · daily archive to bank's S3" />
          <Row k="Search" v="Filter by buyer/seller/action/date · UI on /admin/timeline" />
          <Row k="Integrity" v="Hash chain · last-record digest published in monthly attestation report" />
        </ul>
      </div>
      <div className="rounded-xl border border-white/10 bg-[#070910] p-5">
        <div className="text-[11.5px] uppercase tracking-wide text-white/55">Sample event payload</div>
        <pre className="mt-4 text-[12px] font-mono text-white/85 overflow-x-auto whitespace-pre leading-relaxed">{`{
  "id": "aud_01HM7Y9X9F2K3P0V8E2",
  "at": "2026-02-08T18:03:09.421Z",
  "actor": { "type": "user", "id": "buyer_a8f2", "role": "buyer" },
  "action": "credit.decision.issued",
  "resource": "credit_line:cl_9d2e",
  "status": "success",
  "ip": "73.114.0.0/16",
  "ua": "Chrome/124 macOS",
  "meta": {
    "decision": "approved",
    "limit_cents": 2500000,
    "term_days": 30,
    "model": "claude-sonnet-4.5",
    "rationale_id": "rat_01HM..."
  },
  "prev_hash": "sha256:af23..."
}`}</pre>
      </div>
    </div>
  );
}

function TxMonPanel() {
  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="bank-demo-panel-txmon">
      <div>
        <h3 className="text-lg font-semibold tracking-tight">Transaction monitoring</h3>
        <p className="mt-2 text-[14px] text-white/65 leading-relaxed">
          Every order, invoice, payment, payout, and wallet movement is scored in real time against a layered rule
          set. Hits are routed into a queue with case management; high-severity hits page the on-call compliance
          analyst within minutes.
        </p>
        <ul className="mt-4 space-y-3 text-[13.5px]">
          <Row k="Ingest" v="Synchronous on every state transition · async for batch enrichment" />
          <Row k="Rules" v="Velocity, structuring, geography, peer-clustering, BIN-mismatch, BSA/AML 314(b)" />
          <Row k="Models" v="Verqo AI-scored anomaly explanation per flagged invoice · stored alongside the case" />
          <Row k="Cases" v="Triage queue · 4-eyes approval for AML hits · escalation SLA monitored" />
          <Row k="Reporting" v="Monthly SAR-ready bundle to bank · CTR-eligible flags surfaced inline" />
        </ul>
      </div>
      <div className="rounded-xl border border-white/10 bg-[#070910] p-5">
        <div className="text-[11.5px] uppercase tracking-wide text-white/55">Live alert queue · Q4 2025 sample</div>
        <ul className="mt-4 space-y-2.5 text-[13px]">
          {[
            { sev: "high",    title: "Structuring · 9 invoices · $9,950 each", sub: "Buyer acme-9d2e · within 72h" },
            { sev: "med",     title: "New geo · payout to GB-IBAN",            sub: "Seller glow-3a · seller-of-record matches; cleared" },
            { sev: "med",     title: "Velocity · 14 cards in 24h",             sub: "Buyer luxe-21 · forced step-up; passed Twilio OTP" },
            { sev: "low",     title: "BIN-country mismatch",                   sub: "Card BIN US, billing GB · cleared by analyst" },
            { sev: "low",     title: "Peer cluster anomaly",                   sub: "Order 12× peer median · awaiting buyer notes" },
          ].map((a, i) => {
            const tone = {
              high: "border-rose-500/40 bg-rose-500/10 text-rose-300",
              med:  "border-amber-500/30 bg-amber-500/10 text-amber-300",
              low:  "border-white/15 bg-white/5 text-white/70",
            }[a.sev];
            return (
              <li key={i} className={`rounded-lg border ${tone} px-3 py-2.5`}>
                <div className="text-[13px] font-medium text-white">{a.title}</div>
                <div className="text-[11.5px] mt-0.5 leading-snug">{a.sub}</div>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <li className="grid grid-cols-[140px_1fr] gap-3">
      <span className="text-white/55 text-[12.5px]">{k}</span>
      <span className="text-white/85 text-[13px] leading-snug">{v}</span>
    </li>
  );
}
